import dynamic from 'next/dynamic'
import React from 'react'
import classNames from 'classnames'
import {
  useUserSettings,
} from '@dustin-web/microsite-components/src/hooks'
import { FormattedCurrency, useI18n } from '@dustin-web/microsite-components/src/i18n'
import { getRowPrice } from '@dustin-web/microsite-data/src/utils'
import useShoppingCart from '@dustin-web/product-components/src/hooks/dataloader/use-shopping-cart'
import {
  useSuggestedDeliveryFee,
} from '@dustin-web/microsite-baselayout/src/components/header/cart/mini-cart-utils'
import { AvailabilityStatus } from '@dustin-web/microsite-data/src/graphql/product/generated/graphql-types'
import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'

const CartShippingBannerWrapper = dynamic(() => import('./cart-shipping-banner-wrapper'))

type SummaryRow = {
  label: string
  amount: number
  hidden?: boolean
  isCampaign?: boolean
}

export type SummaryRowVAT = {
  amountIncludingVAT: number
  amountExcludingVAT: number
} & SummaryRow

type Props = {
  additionalSummaryRows?: SummaryRowVAT[]
  fetchSuggestedDeliveryMethod?: boolean
  showDiscount?: boolean
  className?: string
}

export const CartSummary = ({
  additionalSummaryRows,
  fetchSuggestedDeliveryMethod = true,
  showDiscount = false,
  className,
}: Props) => {
  const { totalPrice, totalQuantity, totalPriceIncludingVat, totalVat, cartItems } =
    useShoppingCart()
  const { showPricesInclVat } = useUserSettings()
  const { translate, incExVatTranslation } = useI18n()
  const {
    suggestedDeliveryFee,
    shippingFeeVat,
    suggestedDeliveryFeeIncludingVat,
    suggestedDeliveryInsuranceFee,
  } = useSuggestedDeliveryFee()

  const { enabled: isShowExVatTextFor0VatEnabled } = useFeatureFlag('show-ex-vat-text-for-0-vat')

  const totalDiscount = cartItems.reduce((acc, item) => {
    if (!item.product?.price) return acc
    return (
      acc +
      (item.product.price.isCampaign
        ? showPricesInclVat
          ? item.product.price.originalPriceIncludingVat - item.product.price.priceIncludingVat
          : item.product.price.originalPriceExcludingVat - item.product.price.priceExcludingVat
        : 0)
    )
  }, 0)

  const additionalSummaryRowsFee =
    additionalSummaryRows?.reduce(
      (acc, row) => acc + (showPricesInclVat ? row.amountIncludingVAT : row.amountExcludingVAT),
      0
    ) ?? 0
  const additionalSummaryRowsVat =
    additionalSummaryRows?.reduce(
      (acc, row) => acc + row.amountIncludingVAT - row.amountExcludingVAT,
      0
    ) ?? 0

  const totalCost =
    totalPrice + suggestedDeliveryFee + suggestedDeliveryInsuranceFee + additionalSummaryRowsFee

  const totalQuantityLabel = (quantity: number) =>
    `${quantity} ${quantity === 1 ? translate('Cart_Product') : translate('Cart_Products')}`
  const shippingFromLabel = translate('Cart_ShippingFrom')
  const showPriceIncVatLabel = translate(showPricesInclVat ? 'Cart_OrderVat' : 'Cart_Vat')
  const labelsWithAmmountAllowedToBeZero = [shippingFromLabel, showPriceIncVatLabel]

  const summaryRows: SummaryRow[] = [
    { label: totalQuantityLabel(totalQuantity), amount: totalPrice },
    ...(additionalSummaryRows ?? []),
    ...(fetchSuggestedDeliveryMethod
      ? [
        { label: shippingFromLabel, amount: suggestedDeliveryFee },
        { label: translate('Cart_ShippingInsurance'), amount: suggestedDeliveryInsuranceFee },
      ]
      : []),
    ...(showDiscount && totalDiscount > 0
      ? [{ label: translate('Cart_Discount'), amount: totalDiscount, isCampaign: true }]
      : []),
    {
      label: showPriceIncVatLabel,
      amount: totalVat + shippingFeeVat + additionalSummaryRowsVat,
    },
    {
      label: translate('Cart_IncludingVatShort'),
      amount: totalPriceIncludingVat + suggestedDeliveryFeeIncludingVat,
      hidden: showPricesInclVat,
    },
  ].filter(
    ({ amount, label, hidden }) =>
      !hidden && (labelsWithAmmountAllowedToBeZero.includes(label) || amount > 0)
  )

  const showVatAndNonVatSummaryBoxes =
    showPricesInclVat &&
    isShowExVatTextFor0VatEnabled &&
    cartItems.some(x => x.product?.price?.priceVat === 0) &&
    cartItems.some(
      x => typeof x.product?.price?.priceVat === 'number' && x.product.price.priceVat > 0
    )

  let vatSummaryRows: SummaryRow[] = []
  let zeroVatSummaryRows: SummaryRow[] = []
  let totalPriceForProductsWithoutVat = 0
  let totalPriceForProductsWithVat = 0

  if (additionalSummaryRows) {
    totalPriceForProductsWithoutVat += additionalSummaryRows.reduce(
      (acc, row) => acc + row.amountExcludingVAT,
      0
    )
    totalPriceForProductsWithVat += additionalSummaryRows.reduce(
      (acc, row) => acc + row.amountIncludingVAT,
      0
    )
  }

  if (showVatAndNonVatSummaryBoxes) {
    const getCartSummaries = (filterZeroVat: boolean) => {
      const filteredItems = cartItems.filter(
        x =>
          typeof x.product?.price?.priceVat === 'number' &&
          (filterZeroVat ? x.product.price.priceVat === 0 : x.product.price.priceVat > 0)
      )

      const result = filteredItems.reduce(
        (acc, item) => {
          const totalPrice =
            acc.totalPrice +
            (item.product?.price
              ? getRowPrice(item.product.price, showPricesInclVat, item.quantity)
              : 0)
          const totalPriceExcludingVat =
            acc.totalPriceExcludingVat +
            (item.product?.price?.priceExcludingVat ?? 0) * item.quantity
          const totalPriceIncludingVat =
            acc.totalPriceIncludingVat +
            (item.product?.price?.priceIncludingVat ?? 0) * item.quantity

          const totalVat = totalPriceIncludingVat - totalPriceExcludingVat

          return {
            totalPrice,
            totalPriceExcludingVat,
            totalPriceIncludingVat,
            totalVat,
          }
        },
        { totalPrice: 0, totalPriceExcludingVat: 0, totalPriceIncludingVat: 0, totalVat: 0 }
      )
      return { ...result, totalProducts: filteredItems.length }
    }

    const zeroVatSummaries = getCartSummaries(true)
    const vatSummaries = getCartSummaries(false)
    totalPriceForProductsWithVat = vatSummaries.totalPrice
    totalPriceForProductsWithoutVat = zeroVatSummaries.totalPrice

    vatSummaryRows = [
      {
        label: totalQuantityLabel(vatSummaries.totalProducts),
        amount: vatSummaries.totalPrice,
      },
      {
        label: showPriceIncVatLabel,
        amount: vatSummaries.totalVat,
      },
    ]

    zeroVatSummaryRows = [
      {
        label: totalQuantityLabel(zeroVatSummaries.totalProducts),
        amount: zeroVatSummaries.totalPrice,
      },
      {
        label: showPriceIncVatLabel,
        amount: zeroVatSummaries.totalVat,
      },
    ]
  }

  const allItemsCanBeShippedImmediately = cartItems.length > 0 && cartItems.every((item) => {
    const { product } = item ?? {}

    if (product?.nonReturnable) {
      return false
    }

    return product?.availability?.availabilityStatus === AvailabilityStatus.ImmediateShipping
  })


  return (
    <>
      {allItemsCanBeShippedImmediately && <CartShippingBannerWrapper />}
      {showVatAndNonVatSummaryBoxes && (
        <>
          <div className="c-shoppingcart__summary u-mb-16">
            {vatSummaryRows.map(({ label, amount }) => (
              <div className="c-shoppingcart__summary-row" key={label}>
                <div className="c-shoppingcart__summary-title">{label}</div>
                <div className="c-shoppingcart__summary-text">
                  <FormattedCurrency amount={amount} />
                </div>
              </div>
            ))}
            {/* Summary title */}
            <div className="c-shoppingcart__summary-row c-shoppingcart__summary-row--main">
              <div className="c-shoppingcart__summary-title">
                <strong>
                  <span>
                    {translate('Cart_Total')} {translate('IncludingVatShort')}
                  </span>
                </strong>
              </div>
              <div className="c-shoppingcart__summary-text d-block">
                <strong>
                  <span className="d-block">
                    <FormattedCurrency amount={totalPriceForProductsWithVat} />
                  </span>
                </strong>
              </div>
            </div>
          </div>

          <div className="c-shoppingcart__summary u-mb-16">
            {zeroVatSummaryRows.map(({ label, amount }) => (
              <div className="c-shoppingcart__summary-row" key={label}>
                <div className="c-shoppingcart__summary-title">{label}</div>
                <div className="c-shoppingcart__summary-text">
                  <FormattedCurrency amount={amount} />
                </div>
              </div>
            ))}

            {/* Summary title */}
            <div className="c-shoppingcart__summary-row c-shoppingcart__summary-row--main">
              <div className="c-shoppingcart__summary-title">
                <strong>
                  <span>
                    {translate('Cart_Total')} {translate('ExcludingVatShort')}
                  </span>
                </strong>
              </div>
              <div className="c-shoppingcart__summary-text d-block">
                <strong>
                  <span className="d-block">
                    <FormattedCurrency amount={totalPriceForProductsWithoutVat} />
                  </span>
                </strong>
              </div>
            </div>
          </div>
        </>
      )}

      <div className={classNames('c-shoppingcart__summary', className)}>
        {summaryRows.map(({ label, amount, isCampaign = false }) => (
          <div className="c-shoppingcart__summary-row u-px-0" key={label}>
            <div className="c-shoppingcart__summary-title">{label}</div>
            <div className="c-shoppingcart__summary-text">
              {isCampaign ? '-' : ''}
              <FormattedCurrency amount={amount} />
            </div>
          </div>
        ))}

        {/* Summary title */}
        <div className="c-shoppingcart__summary-row c-shoppingcart__summary-row--main u-px-0">
          <div className="c-shoppingcart__summary-title">
            <strong>
              <span>{translate('Cart_Total')}</span>
            </strong>
          </div>
          <div className="c-shoppingcart__summary-text d-block">
            <strong>
              <span className="d-block">
                <FormattedCurrency amount={totalCost} />
              </span>
              <small>{!showVatAndNonVatSummaryBoxes ? incExVatTranslation(totalVat) : null}</small>
            </strong>
          </div>
        </div>
      </div>
    </>
  )
}

